import type { CacheEngine, CacheOptions } from '~/shared/interfaces'

interface StoredValue<V> {
  value: V
  expiresAt?: number
}

const DEFAULT_LIMIT = 1000

export class LruCache<V, K extends string = string> implements CacheEngine<V, K> {
  private readonly store = new Map<K, StoredValue<V>>()
  private readonly limit: number
  private readonly lifetimeMs: number = 0

  constructor(options: CacheOptions = {}) {
    this.limit = options.limit ?? DEFAULT_LIMIT
    if (options.lifetimeSeconds && options.lifetimeSeconds > 0) {
      this.lifetimeMs = options.lifetimeSeconds * 1000
    }
  }

  get(key: K): V | undefined {
    const stored = this.store.get(key)
    if (stored === undefined) {
      return undefined
    }

    this.store.delete(key)

    if (stored.expiresAt !== undefined && stored.expiresAt < Date.now()) {
      return undefined
    }

    this.store.set(key, stored)
    return stored.value
  }

  set(key: K, value: V): void {
    const stored: StoredValue<V> = { value }
    if (this.lifetimeMs) {
      stored.expiresAt = Date.now() + this.lifetimeMs
    }

    this.store.delete(key)
    this.store.set(key, stored)

    if (this.store.size > this.limit) {
      const oldestKey = this.store.keys().next().value
      if (oldestKey !== undefined) {
        this.store.delete(oldestKey)
      }
    }
  }

  delete(key: K): void {
    this.store.delete(key)
  }

  clear(): void {
    this.store.clear()
  }
}
